export type InstitutionMetricKey =
  | 'activePrograms'
  | 'participants'
  | 'recordTemplates'
  | 'activeReviewers'
  | 'pendingSubmissions'
  | 'recordsIssued'
  | 'publicRecords'
  | 'archiveCollections'
  | 'publicArchiveCollections'
  | 'archiveEntries'
  | 'publicArchiveEntries'
  | 'contributors'
  | 'publicStories'
  | 'publicVerificationViews'
  | 'revokedRecords'
  | 'expiredRecords';

export type InstitutionMetrics = Record<InstitutionMetricKey, number>;

export const EMPTY_INSTITUTION_METRICS: InstitutionMetrics = {
  activePrograms: 0,
  participants: 0,
  recordTemplates: 0,
  activeReviewers: 0,
  pendingSubmissions: 0,
  recordsIssued: 0,
  publicRecords: 0,
  archiveCollections: 0,
  publicArchiveCollections: 0,
  archiveEntries: 0,
  publicArchiveEntries: 0,
  contributors: 0,
  publicStories: 0,
  publicVerificationViews: 0,
  revokedRecords: 0,
  expiredRecords: 0,
};

export type CanonicalInstitutionMetricRow = {
  active_programs?: number | string | null;
  participants?: number | string | null;
  record_templates?: number | string | null;
  active_reviewers?: number | string | null;
  pending_submissions?: number | string | null;
  records_issued?: number | string | null;
  public_records?: number | string | null;
  archive_collections?: number | string | null;
  public_archive_collections?: number | string | null;
  archive_entries?: number | string | null;
  public_archive_entries?: number | string | null;
  contributors?: number | string | null;
  public_stories?: number | string | null;
  public_verification_views?: number | string | null;
  revoked_records?: number | string | null;
  expired_records?: number | string | null;
};

function normalizeCount(value: number | string | null | undefined): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function mapCanonicalInstitutionMetrics(
  row: CanonicalInstitutionMetricRow | null
): InstitutionMetrics {
  if (!row) return { ...EMPTY_INSTITUTION_METRICS };
  return {
    activePrograms: normalizeCount(row.active_programs),
    participants: normalizeCount(row.participants),
    recordTemplates: normalizeCount(row.record_templates),
    activeReviewers: normalizeCount(row.active_reviewers),
    pendingSubmissions: normalizeCount(row.pending_submissions),
    recordsIssued: normalizeCount(row.records_issued),
    publicRecords: normalizeCount(row.public_records),
    archiveCollections: normalizeCount(row.archive_collections),
    publicArchiveCollections: normalizeCount(row.public_archive_collections),
    archiveEntries: normalizeCount(row.archive_entries),
    publicArchiveEntries: normalizeCount(row.public_archive_entries),
    contributors: normalizeCount(row.contributors),
    publicStories: normalizeCount(row.public_stories),
    publicVerificationViews: normalizeCount(row.public_verification_views),
    revokedRecords: normalizeCount(row.revoked_records),
    expiredRecords: normalizeCount(row.expired_records),
  };
}

export const INSTITUTION_METRIC_DEFINITIONS: Record<
  InstitutionMetricKey,
  { label: string; source: string; calculation: string; public: boolean }
> = {
  activePrograms: {
    label: 'Active programs',
    source: 'institution_programs',
    calculation: "Rows for the institution where status = 'active'",
    public: false,
  },
  participants: {
    label: 'Participants',
    source: 'institution_participants',
    calculation: 'All roster rows scoped to the institution',
    public: false,
  },
  recordTemplates: {
    label: 'Record templates',
    source: 'institution_record_templates',
    calculation: 'Institution-owned, non-preset templates',
    public: false,
  },
  activeReviewers: {
    label: 'Active reviewers',
    source: 'institution_members',
    calculation: 'Institution members with verifier or administrator authority',
    public: false,
  },
  pendingSubmissions: {
    label: 'Pending submissions',
    source: 'institution_submission_requests + credential_trust_records',
    calculation:
      'Submission requests in pending/under_review plus records in pending/under_review/needs_info',
    public: false,
  },
  recordsIssued: {
    label: 'Records issued',
    source: 'credential_trust_records',
    calculation: "Rows for the institution where review_status = 'approved'",
    public: false,
  },
  publicRecords: {
    label: 'Public Trust Pages',
    source: 'credential_trust_records',
    calculation: "Approved records with visibility = 'public' and lifecycle_status = 'active'",
    public: true,
  },
  archiveCollections: {
    label: 'Archive collections',
    source: 'heritage_collections',
    calculation: 'Institution-owned collections',
    public: false,
  },
  publicArchiveCollections: {
    label: 'Public archive collections',
    source: 'heritage_collections',
    calculation: "Institution-owned collections where visibility = 'public'",
    public: true,
  },
  archiveEntries: {
    label: 'Archive entries',
    source: 'heritage_archive_items',
    calculation: 'Institution-owned archive items',
    public: false,
  },
  publicArchiveEntries: {
    label: 'Public archive entries',
    source: 'heritage_archive_items',
    calculation:
      "Institution-owned items where visibility = 'public' and review_status = 'published'",
    public: true,
  },
  contributors: {
    label: 'Team contributors',
    source: 'institution_members',
    calculation: 'Institution membership rows excluding the owner account',
    public: false,
  },
  publicStories: {
    label: 'Public stories',
    source: 'community_posts',
    calculation:
      'Institution posts that are published, public, and explicitly marked as institution content',
    public: true,
  },
  publicVerificationViews: {
    label: 'Public verification views',
    source: 'credential_trust_records.view_count',
    calculation: 'Sum of view_count for public active Trust Pages',
    public: false,
  },
  revokedRecords: {
    label: 'Revoked records',
    source: 'credential_trust_records',
    calculation: "Rows for the institution where lifecycle_status = 'revoked'",
    public: false,
  },
  expiredRecords: {
    label: 'Expired records',
    source: 'credential_trust_records',
    calculation:
      "Rows where lifecycle_status = 'expired', or active rows whose expires_at is in the past",
    public: false,
  },
};

export type InstitutionReadinessInput = {
  hasIdentity: boolean;
  hasLogo: boolean;
  profilePublished: boolean;
  hasProgram: boolean;
  hasTemplate: boolean;
  hasReviewer: boolean;
  intakeConfigured: boolean;
  hasPublishedContent: boolean;
};

export type InstitutionReadinessFactor = {
  id: keyof InstitutionReadinessInput;
  label: string;
  complete: boolean;
};

export type InstitutionReadiness = {
  state: 'Setup required' | 'Ready to configure' | 'Active' | 'Needs review';
  completed: number;
  total: number;
  factors: InstitutionReadinessFactor[];
};

const READINESS_LABELS: Record<keyof InstitutionReadinessInput, string> = {
  hasIdentity: 'Institution identity completed',
  hasLogo: 'Institution logo added',
  profilePublished: 'Public profile published',
  hasProgram: 'Program created',
  hasTemplate: 'Record template configured',
  hasReviewer: 'Reviewer or verifier assigned',
  intakeConfigured: 'Submission intake configured',
  hasPublishedContent: 'Public record or archive published',
};

export function calculateInstitutionReadiness(
  input: InstitutionReadinessInput,
  options?: { hasPendingReview?: boolean }
): InstitutionReadiness {
  const factors = (Object.keys(READINESS_LABELS) as (keyof InstitutionReadinessInput)[]).map(
    (id) => ({ id, label: READINESS_LABELS[id], complete: input[id] })
  );
  const completed = factors.filter((factor) => factor.complete).length;

  let state: InstitutionReadiness['state'];
  if (options?.hasPendingReview && completed >= 5) state = 'Needs review';
  else if (completed === factors.length) state = 'Active';
  else if (completed >= 3) state = 'Ready to configure';
  else state = 'Setup required';

  return { state, completed, total: factors.length, factors };
}
